import type { NodeEntry, Range } from 'slate';

import type { RenderInlineOptions } from './inline';
import { renderInlineFns } from './inline';
import type { RenderModelSearchHit } from './paragraph';
import { renderText } from './text';
import { isRenderModelText, Queries } from '../../slate-api';
import type {
  AlineText,
  DeleteParagraphEdit,
  RenderModelLine,
  SuggestedParagraphGlyphEdit,
} from '../../slate-api';
import { maxValueBy } from '../../util';
import type { RenderContext } from '../engine/render-context';
import type { BaseRenderNodeOptions } from '../engine/rendering-data';

const PILCROW = '¶';

/**
 * A render-only paragraph mark (¶) drawn at the end of a paragraph, either for a
 * suggested `deleteParagraph` edit (author color + strikethrough via `inheritDeleteEdit`)
 * or when "show non-printing-character" is on
 */
export interface SuggestedParagraphMark {
  /** Non-printing-character (blue) fill for the ¶; unset for a deletion (author color). */
  color?: string;
  deleteEdit?: DeleteParagraphEdit;
  selectionHighlightColor?: string;
}

export interface RenderLineOptions extends BaseRenderNodeOptions {
  rMSearchHits: RenderModelSearchHit[];
  annotationMarkerGlyphEdits?: SuggestedParagraphGlyphEdit[];
  rMSpellcheckHits?: Range[];
  suggestedParagraphMark?: SuggestedParagraphMark;
}

export function renderLine(
  renderContext: RenderContext,
  rMLineEntry: NodeEntry<RenderModelLine>,
  options: RenderLineOptions
) {
  const [rMLine] = rMLineEntry;
  const { baselinePx, children, heightPx, isLastLine, maxDescentPx, spaceWidthPx } = rMLine;
  const { rMSearchHits, rMSpellcheckHits } = options;

  const maxFontSizePx = maxValueBy(children.filter(isRenderModelText), Queries.renderedFontSize);

  renderContext.renderChildren(rMLineEntry, renderInlineFns, {
    ...options,
    baselinePx,
    lineHeightPx: heightPx,
    lineMaxDescentPx: maxDescentPx,
    lineMaxFontSizePx: maxFontSizePx,
    rMSearchHits,
    rMSpellcheckHits,
    spaceWidthPx: spaceWidthPx,
  } satisfies RenderInlineOptions);

  if (isLastLine) {
    /**
     * Emit the right-margin annotation marker for the paragraph's glyph edits
     */
    if (options.annotationMarkerGlyphEdits?.length)
      renderContext.drawAnnotationMarker({ suggestedEdits: options.annotationMarkerGlyphEdits });

    if (options.suggestedParagraphMark)
      renderSuggestedParagraphMark(renderContext, rMLine, options.suggestedParagraphMark, options);
  }
}

/**
 * Draws the ¶ symbol at the end of paragraph for suggested paragraph deletions
 * or when we show non-printing characters
 */
function renderSuggestedParagraphMark(
  renderContext: RenderContext,
  rMLine: RenderModelLine,
  mark: SuggestedParagraphMark,
  options: BaseRenderNodeOptions
) {
  const { baselinePx, children, heightPx, maxDescentPx, widthPx } = rMLine;
  /**
   * We use text props for rendering ¶ from the last paragraph text node to match styling and size
   * The correct way of doing it is by using glyphProps instead, but we must implement
   * a correct glyphProps editing first, along with suggested edits support.
   */
  const lastTextRun = children.findLast(isRenderModelText);
  if (!lastTextRun) return;

  const pilcrowNode: AlineText = {
    ...Queries.getActiveTextProps(lastTextRun),
    comments: [],
    suggestedEdits: [],
    text: PILCROW,
  };

  renderContext.renderSpecial(widthPx, 0, () => {
    renderText(renderContext, [pilcrowNode], {
      ...options,
      baselinePx,
      customHighlight: mark.selectionHighlightColor
        ? {
            color: mark.selectionHighlightColor,
            debugName: 'pilcrowSelectionHighlight',
            height: 'endOfLine',
          }
        : undefined,
      inheritDeleteEdit: mark.deleteEdit,
      lineHeightPx: heightPx,
      lineMaxDescentPx: maxDescentPx,
      lineMaxFontSizePx: Queries.renderedFontSize(pilcrowNode),
      overrideColor: mark.color,
      rMSearchHits: [],
      spaceWidthPx: 'default',
    });
  });
}
